import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Search, Package, Loader2, CheckSquare, Square } from 'lucide-react';
import DeliveriesTable from './DeliveriesTable';
import ProviderStatisticsCards from './ProviderStatisticsCards';

const API_URL = process.env.REACT_APP_API_URL;

function ProvidersAccounting({ userId, selectedDate }) {
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [userType, setUserType] = useState('linked'); // 'linked' or 'dropship'
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchProviders();
  }, [selectedDate]);

  async function fetchProviders() {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/accounting/providers`, {
        params: { user_id: userId, date: selectedDate }
      });
      setProviders(response.data);
    } catch (error) {
      console.error('Error fetching providers:', error);
    } finally {
      setLoading(false);
    }
  }

  async function fetchProviderDeliveries(search = '') {
    if (selectedUsers.length === 0) return;

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/accounting/provider/deliveries`,
        {
          admin_id: userId,
          date: selectedDate,
          user_ids: selectedUsers,
          provider_id: selectedProvider.id,
          user_type: userType,
          search: search
        }
      );
      
      const combined = [
        ...response.data.lima_deliveries,
        ...response.data.provincia_deliveries
      ];
      setDeliveries(combined);
      setStatistics(response.data.statistics);
    } catch (error) {
      console.error('Error fetching deliveries:', error);
    } finally {
      setLoading(false);
    }
  }

  function handleProviderSelect(provider) {
    setSelectedProvider(provider);
    setUserType('linked');
    setSelectedUsers([]);
    setDeliveries([]);
    setStatistics(null);
  }

  function handleUserTypeChange(type) {
    setUserType(type);
    setSelectedUsers([]);
    setDeliveries([]);
    setStatistics(null);
  }

  function handleUserToggle(userId) {
    setSelectedUsers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  }

  function handleSelectAll() {
    const currentUsers = userType === 'linked' 
      ? selectedProvider.linked_users 
      : selectedProvider.dropship_users;
    
    if (selectedUsers.length === currentUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(currentUsers.map(u => u.id));
    }
  }

  function handleLoadData() {
    fetchProviderDeliveries('');
  }

  function handleSearch(search) {
    fetchProviderDeliveries(search);
  }

  function handleExport() {
    const data = {
      admin_id: userId,
      deliveries: deliveries,
      statistics: statistics,
      type: 'provider',
      title: `Contabilidad - ${selectedProvider?.name || 'Proveedor'} - ${userType === 'linked' ? 'Usuarios Vinculados' : 'Dropshipping'} - ${selectedDate}`
    };

    axios.post(`${API_URL}/api/accounting/export`, data, {
      responseType: 'blob'
    })
      .then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `contabilidad_${selectedProvider?.name}_${userType}_${selectedDate}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      })
      .catch(error => {
        console.error('Error exporting:', error);
      });
  }

  if (!selectedProvider) {
    return (
      <div>
        <div className="mb-4">
          <h2 className="text-xl font-bold text-slate-800 font-montserrat mb-4">
            Selecciona un Proveedor
          </h2>
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
            </div>
          ) : providers.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 font-montserrat">
                No hay proveedores con entregas en esta fecha
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {providers.map((provider) => (
                <button
                  key={provider.id}
                  onClick={() => handleProviderSelect(provider)}
                  className="bg-white border-2 border-slate-200 rounded-xl p-4 hover:border-brand-primary hover:shadow-lg transition-all text-left group"
                >
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-bold text-slate-800 font-montserrat group-hover:text-brand-primary transition-colors">
                      {provider.name}
                    </h3>
                    <div className="bg-brand-gold/20 text-brand-gold px-3 py-1 rounded-full text-sm font-bold font-montserrat">
                      {provider.linked_users.length + provider.dropship_users.length}
                    </div>
                  </div>
                  <div className="flex gap-2 text-xs font-montserrat">
                    {provider.linked_users.length > 0 && (
                      <span className="bg-brand-primary/10 text-brand-primary px-2 py-1 rounded">
                        {provider.linked_users.length} vinculado{provider.linked_users.length !== 1 ? 's' : ''}
                      </span>
                    )}
                    {provider.dropship_users.length > 0 && (
                      <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded">
                        {provider.dropship_users.length} dropship
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  const currentUsers = userType === 'linked' 
    ? selectedProvider.linked_users 
    : selectedProvider.dropship_users;

  if (deliveries.length === 0 && !statistics) {
    return (
      <div>
        {/* Provider Header */}
        <div className="mb-6 bg-gradient-to-r from-brand-gold to-brand-gold/80 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold font-montserrat">
              {selectedProvider.name}
            </h2>
            <button
              onClick={() => setSelectedProvider(null)}
              className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-montserrat font-semibold transition-all"
            >
              Volver
            </button>
          </div>
        </div>

        {/* User Type Tabs */}
        <div className="mb-6 bg-white rounded-xl border-2 border-slate-200 p-2 flex gap-2">
          <button
            onClick={() => handleUserTypeChange('linked')}
            className={`flex-1 py-3 px-4 rounded-lg font-montserrat font-semibold transition-all ${
              userType === 'linked'
                ? 'bg-brand-primary text-white'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            Usuarios Vinculados ({selectedProvider.linked_users.length})
          </button>
          <button
            onClick={() => handleUserTypeChange('dropship')}
            className={`flex-1 py-3 px-4 rounded-lg font-montserrat font-semibold transition-all ${
              userType === 'dropship'
                ? 'bg-purple-600 text-white'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            Dropshipping ({selectedProvider.dropship_users.length})
          </button>
        </div>

        {/* User Selection */}
        <div className="bg-white rounded-xl border-2 border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-slate-800 font-montserrat">
              Selecciona Usuarios {userType === 'dropship' && '(Dropshipping)'}
            </h3>
            <button
              onClick={handleSelectAll}
              className="text-sm text-brand-primary hover:text-brand-primary/80 font-montserrat font-semibold"
            >
              {selectedUsers.length === currentUsers.length ? 'Deseleccionar' : 'Seleccionar'} Todos
            </button>
          </div>

          {currentUsers.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-slate-500 font-montserrat">
                No hay usuarios {userType === 'dropship' ? 'de dropshipping' : 'vinculados'} con entregas en esta fecha
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
                {currentUsers.map((user) => (
                  <button
                    key={user.id}
                    onClick={() => handleUserToggle(user.id)}
                    className={`
                      p-4 rounded-lg border-2 transition-all text-left flex items-start gap-3
                      ${selectedUsers.includes(user.id)
                        ? 'border-brand-primary bg-brand-primary/5'
                        : 'border-slate-200 hover:border-brand-primary/50'
                      }
                    `}
                  >
                    <div className="mt-0.5">
                      {selectedUsers.includes(user.id) ? (
                        <CheckSquare className="w-5 h-5 text-brand-primary" />
                      ) : (
                        <Square className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-bold text-slate-800 font-montserrat truncate">
                        {user.username}
                      </h4>
                      <p className="text-xs text-slate-500 truncate">{user.email}</p>
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-slate-400 font-montserrat">
                          {user.delivery_count} total
                        </span>
                        {user.lima_count > 0 && (
                          <span className="text-xs bg-brand-primary/10 text-brand-primary px-2 py-0.5 rounded">
                            Lima: {user.lima_count}
                          </span>
                        )}
                        {user.provincia_count > 0 && (
                          <span className="text-xs bg-brand-gold/10 text-brand-gold px-2 py-0.5 rounded">
                            Prov: {user.provincia_count}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              <button
                onClick={handleLoadData}
                disabled={selectedUsers.length === 0}
                className={`
                  w-full py-3 rounded-lg font-montserrat font-bold transition-all
                  ${selectedUsers.length === 0
                    ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                    : 'bg-brand-primary text-white hover:bg-brand-primary/90'
                  }
                `}
              >
                Cargar Datos ({selectedUsers.length} usuario{selectedUsers.length !== 1 ? 's' : ''})
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Provider Header */}
      <div className="mb-6 bg-gradient-to-r from-brand-gold to-brand-gold/80 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold font-montserrat mb-1">
              {selectedProvider.name}
            </h2>
            <p className="text-white/90 text-sm">
              {selectedUsers.length} usuario{selectedUsers.length !== 1 ? 's' : ''} • {userType === 'linked' ? 'Vinculados' : 'Dropshipping'}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => {
                setDeliveries([]);
                setStatistics(null);
              }}
              className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-montserrat font-semibold transition-all"
            >
              Cambiar Usuarios
            </button>
            <button
              onClick={() => setSelectedProvider(null)}
              className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-montserrat font-semibold transition-all"
            >
              Volver
            </button>
          </div>
        </div>
      </div>

      {/* Statistics */}
      {statistics && (
        <ProviderStatisticsCards 
          statistics={statistics} 
          userType={userType}
        />
      )}

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Buscar por ID, nombre o teléfono..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              handleSearch(e.target.value);
            }}
            className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:border-brand-primary transition-all font-montserrat"
          />
        </div>
      </div>

      {/* Deliveries Table */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
        </div>
      ) : (
        <DeliveriesTable 
          limaDeliveries={deliveries.filter(d => d.type === 'Lima')}
          provinciaDeliveries={deliveries.filter(d => d.type === 'Provincia')}
          onExport={handleExport}
          showUserColumn={true}
          isProvider={true}
          userType={userType}
        />
      )}
    </div>
  );
}

export default ProvidersAccounting;